/** @file Recompiles the viewed document incrementally into the compiled store; keeps the last rig on failure. */
import { createCompileCache } from '@pose2d/compiler/incremental/createCompileCache';
import { recompile } from '@pose2d/compiler/incremental/recompile';
import type { TextureLayout } from '@pose2d/compiler/textureLayout';
import { loadSkeleton } from '@pose2d/core/load/loadSkeleton';
import { docIndex } from '@pose2d/format/doc/docIndex';
import type { Doc } from '@pose2d/format/types';
import { compiledStore } from './compiledStore';

const IDENTITY: TextureLayout = { kind: 'identity' };
let cache = createCompileCache();

/**
 * null (project closed) resets the store and the cache; the document already held is a no-op. A compile or load
 * failure keeps the previous rig and records the error (R-E1-19): a committed document always compiles, so only a bad
 * optimistic command gets here, and the server refuses it.
 */
export function syncCompiled(doc: Doc | null): void {
  const state = compiledStore.getState();
  if (doc === state.doc) return;
  if (doc === null) {
    cache = createCompileCache();
    compiledStore.setState(compiledStore.getInitialState(), true);
    return;
  }
  try {
    const { json, atlas, warnings } = recompile(cache, doc, docIndex(doc), IDENTITY);
    compiledStore.setState({ doc, json, atlas, data: loadSkeleton(json, atlas), warnings, error: null }, true);
  } catch (error) {
    compiledStore.setState({ doc, error });
  }
}
